import React, { useCallback, useEffect, useRef, useState } from 'react';
import FCategoryIcon, { resolveFCategory } from '../fCategoryIcons';

const SWIPE_HINT_KEY = 'dv2-goals-swipe-hint-seen';

/**
 * Compact dark-glass goal legend for the V2 dashboard journey card.
 * Readable details live here — not on the path markers.
 * When swipeEnabled + navItems, horizontal swipe cycles the active goal.
 */
export default function GoalJourneyLegend({
    items = [],
    navItems = null,
    swipeEnabled = false,
    activeGoalRank = null,
    onActiveChange,
    onClearActive,
}) {
    const touchRef = useRef({ x: 0, y: 0, active: false });
    const [hintVisible, setHintVisible] = useState(() => {
        if (!swipeEnabled || typeof window === 'undefined') return false;
        try {
            return window.sessionStorage?.getItem(SWIPE_HINT_KEY) !== '1';
        } catch {
            return true;
        }
    });

    const sequence =
        swipeEnabled && Array.isArray(navItems) && navItems.length
            ? navItems
            : null;

    const dismissHint = useCallback(() => {
        setHintVisible(false);
        try {
            window.sessionStorage?.setItem(SWIPE_HINT_KEY, '1');
        } catch {
            /* ignore */
        }
    }, []);

    useEffect(() => {
        if (!swipeEnabled || !hintVisible) return undefined;
        const timer = window.setTimeout(dismissHint, 9000);
        return () => window.clearTimeout(timer);
    }, [swipeEnabled, hintVisible, dismissHint]);

    const goRelative = useCallback(
        (delta) => {
            if (!sequence?.length || !onActiveChange) return;
            const sorted = [...sequence].sort(
                (a, b) => (b.goalNumber || 0) - (a.goalNumber || 0)
            );
            const curIdx = sorted.findIndex(
                (item) => item.rank === activeGoalRank
            );
            const base = curIdx < 0 ? 0 : curIdx;
            const next = (base + delta + sorted.length * 8) % sorted.length;
            onActiveChange(sorted[next].rank);
            dismissHint();
        },
        [sequence, activeGoalRank, onActiveChange, dismissHint]
    );

    const onTouchStart = (e) => {
        if (!sequence) return;
        const t = e.changedTouches?.[0] || e.touches?.[0];
        if (!t) return;
        touchRef.current = { x: t.clientX, y: t.clientY, active: true };
    };

    const onTouchEnd = (e) => {
        if (!sequence || !touchRef.current.active) return;
        touchRef.current.active = false;
        const t = e.changedTouches?.[0];
        if (!t) return;
        const dx = t.clientX - touchRef.current.x;
        const dy = t.clientY - touchRef.current.y;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.15) {
            return;
        }
        // Swipe left → toward summit (7→1); swipe right → toward base
        goRelative(dx < 0 ? 1 : -1);
    };

    if (!items.length) return null;

    const legend = (
        <ol
            className={`dv2-gjourney__legend${
                swipeEnabled ? ' dv2-gjourney__legend--swipe' : ''
            }`}
            aria-label={
                swipeEnabled
                    ? 'Goal legend. Swipe left or right to browse goals.'
                    : 'Goal legend'
            }
            onTouchStart={sequence ? onTouchStart : undefined}
            onTouchEnd={sequence ? onTouchEnd : undefined}
        >
            {items.map((item) => {
                const {
                    rank,
                    goalNumber,
                    title,
                    category,
                    accentColor,
                    isOolaOne,
                } = item;
                const fName = resolveFCategory(category);
                const categoryLabel = (
                    fName ||
                    category ||
                    'Goal'
                ).toUpperCase();
                const isActive = activeGoalRank === rank;
                const aria = [
                    isOolaOne ? 'Oola One' : `Goal ${goalNumber}`,
                    categoryLabel,
                    title || 'Goal',
                    swipeEnabled ? 'Swipe to change goal' : null,
                ]
                    .filter(Boolean)
                    .join('. ');

                return (
                    <li
                        key={`legend-${rank}`}
                        className="dv2-gjourney__legend-li"
                    >
                        <button
                            type="button"
                            className={`dv2-gjourney__legend-item${
                                isOolaOne ? ' is-oola-one' : ''
                            }${isActive ? ' is-active' : ''}`}
                            style={{ ['--dv2-goal-accent']: accentColor }}
                            aria-label={aria}
                            aria-current={isActive ? 'true' : undefined}
                            onMouseEnter={() => onActiveChange?.(rank)}
                            onFocus={() => onActiveChange?.(rank)}
                            onMouseLeave={() => onClearActive?.()}
                            onBlur={() => onClearActive?.()}
                            onClick={() => onActiveChange?.(rank)}
                        >
                            <span
                                className="dv2-gjourney__legend-icon"
                                aria-hidden="true"
                            >
                                <FCategoryIcon
                                    category={category}
                                    size={isOolaOne ? 14 : 12}
                                />
                            </span>
                            <span className="dv2-gjourney__legend-copy">
                                <span className="dv2-gjourney__legend-meta">
                                    {isOolaOne ? (
                                        <>
                                            <span className="dv2-gjourney__legend-oola">
                                                OOLA ONE
                                            </span>
                                            <span className="dv2-gjourney__legend-dot">
                                                ·
                                            </span>
                                            <span className="dv2-gjourney__legend-cat">
                                                {categoryLabel}
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="dv2-gjourney__legend-num">
                                                {goalNumber}
                                            </span>
                                            <span className="dv2-gjourney__legend-cat">
                                                {categoryLabel}
                                            </span>
                                        </>
                                    )}
                                </span>
                                <span className="dv2-gjourney__legend-title">
                                    {title || 'Goal'}
                                </span>
                            </span>
                            {swipeEnabled ? (
                                <span
                                    className="dv2-gjourney__swipe-chevs"
                                    aria-hidden="true"
                                >
                                    <span>‹</span>
                                    <span>›</span>
                                </span>
                            ) : null}
                        </button>
                    </li>
                );
            })}
        </ol>
    );

    if (!swipeEnabled) return legend;

    return (
        <div className="dv2-gjourney__legend-shell">
            <p
                className={`dv2-gjourney__swipe-hint${
                    hintVisible ? ' is-visible' : ''
                }`}
                aria-hidden={!hintVisible}
            >
                <span className="dv2-gjourney__swipe-hint-chev is-left">‹</span>
                Swipe to browse goals
                <span className="dv2-gjourney__swipe-hint-chev is-right">›</span>
            </p>
            {legend}
        </div>
    );
}
